import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Coins, 
  TrendingUp, 
  TrendingDown, 
  Search, 
  Filter, 
  User, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  X,
  Plus,
  Minus,
  History,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { API } from '../utils/api';
import { toast } from 'sonner';

// Modal para ajustar saldo
const AdjustBalanceModal = ({ users, onClose, onSuccess }) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [operation, setOperation] = useState('add');
  const [balanceType, setBalanceType] = useState('coins');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  const filteredUsers = useMemo(() => {
    if (!userSearch) return users;
    const search = userSearch.toLowerCase();
    return users.filter(u => 
      u.name?.toLowerCase().includes(search) || 
      u.email?.toLowerCase().includes(search)
    );
  }, [users, userSearch]);

  const selectedUser = users.find(u => u.id === selectedUserId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      toast.error('Selecione um usuário');
      return;
    }
    
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Informe uma quantidade válida');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/users/${selectedUserId}/adjust-balance`, {
        operation,
        balance_type: balanceType,
        amount: parseFloat(amount),
        reason: reason || null
      });
      
      const operationText = operation === 'add' ? 'adicionado' : 'removido';
      const typeText = balanceType === 'coins' ? 'FerCoins' : 'pontos';
      toast.success(`${amount} ${typeText} ${operationText}(s) com sucesso!`);
      
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao ajustar saldo');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <motion.div
        className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-lg"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-neon-purple/20 rounded-full flex items-center justify-center">
              <Coins className="w-5 h-5 text-neon-purple" />
            </div>
            <h3 className="font-bold text-white text-lg">Ajustar Saldo</h3>
          </div>
          <Button onClick={onClose} variant="ghost" className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Seletor de Usuário */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Usuário</label>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Buscar usuário..."
                className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                data-testid="user-search-input"
              />
            </div>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg p-3"
              data-testid="user-select"
            >
              <option value="">Selecione um usuário...</option>
              {filteredUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
            {selectedUser && (
              <div className="mt-2 p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-sm text-zinc-400">Saldo atual:</p>
                <div className="flex gap-4 mt-1">
                  <span className="text-amber-400 flex items-center gap-1">
                    <img src="/fercoin.png" alt="FerCoin" className="w-4 h-4" />
                    {selectedUser.coins || 0} FerCoins
                  </span>
                  <span className="text-neon-purple flex items-center gap-1">
                    <Sparkles className="w-4 h-4" />
                    {selectedUser.total_xp?.toFixed(0) || 0} XP
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Tipo de Operação */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Operação</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setOperation('add')}
                className={`p-3 rounded-lg border flex items-center justify-center gap-2 transition-all ${
                  operation === 'add' 
                    ? 'bg-green-500/20 border-green-500 text-green-400' 
                    : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                }`}
                data-testid="operation-add-btn"
              >
                <Plus className="w-4 h-4" /> Adicionar
              </button>
              <button
                type="button"
                onClick={() => setOperation('remove')}
                className={`p-3 rounded-lg border flex items-center justify-center gap-2 transition-all ${
                  operation === 'remove' 
                    ? 'bg-red-500/20 border-red-500 text-red-400' 
                    : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                }`}
                data-testid="operation-remove-btn"
              >
                <Minus className="w-4 h-4" /> Remover
              </button>
            </div>
          </div>

          {/* Tipo de Saldo */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Tipo de Saldo</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setBalanceType('coins')}
                className={`p-3 rounded-lg border flex items-center justify-center gap-2 transition-all ${
                  balanceType === 'coins' 
                    ? 'bg-amber-500/20 border-amber-500 text-amber-400' 
                    : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                }`}
                data-testid="type-coins-btn"
              >
                <img src="/fercoin.png" alt="FerCoin" className="w-4 h-4" /> FerCoins
              </button>
              <button
                type="button"
                onClick={() => setBalanceType('points')}
                className={`p-3 rounded-lg border flex items-center justify-center gap-2 transition-all ${
                  balanceType === 'points' 
                    ? 'bg-purple-500/20 border-purple-500 text-purple-400' 
                    : 'bg-zinc-800 border-zinc-700 text-zinc-400'
                }`}
                data-testid="type-points-btn"
              >
                <Sparkles className="w-4 h-4" /> Pontos (XP)
              </button>
            </div>
          </div>

          {/* Quantidade */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Quantidade</label>
            <Input
              type="number"
              min="1"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Ex: 100"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="amount-input"
            />
          </div>

          {/* Motivo */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Motivo (opcional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Bônus por meta atingida, correção de saldo..."
              className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg p-3 min-h-[80px] resize-none"
              data-testid="reason-input"
            />
          </div>

          {/* Botões */}
          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              disabled={isSubmitting || !selectedUserId || !amount}
              className={`flex-1 ${
                operation === 'add' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700'
              } text-white`}
              data-testid="submit-adjustment-btn"
            >
              {isSubmitting ? 'Processando...' : operation === 'add' ? 'Adicionar Saldo' : 'Remover Saldo'}
            </Button>
            <Button type="button" onClick={onClose} className="bg-zinc-800 text-white hover:bg-zinc-700">
              Cancelar
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Card de transação
const TransactionCard = ({ transaction }) => {
  const isAdd = transaction.operation === 'add';
  const isCoins = transaction.balance_type === 'coins';

  return (
    <motion.div
      className="bg-zinc-950/50 border border-white/5 rounded-xl p-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isAdd ? 'bg-green-500/20' : 'bg-red-500/20'
          }`}>
            {isAdd ? (
              <TrendingUp className={`w-5 h-5 ${isAdd ? 'text-green-400' : 'text-red-400'}`} />
            ) : (
              <TrendingDown className="w-5 h-5 text-red-400" />
            )}
          </div>
          <div>
            <p className="font-medium text-white">{transaction.user_name}</p>
            <p className="text-xs text-zinc-500">{transaction.user_email}</p>
          </div>
        </div>
        <div className="text-right">
          <p className={`font-bold ${isAdd ? 'text-green-400' : 'text-red-400'}`}>
            {isAdd ? '+' : '-'}{transaction.amount.toFixed(0)} {isCoins ? 'FerCoins' : 'XP'}
          </p>
          <p className="text-xs text-zinc-500">
            {transaction.previous_balance.toFixed(0)} → {transaction.new_balance.toFixed(0)}
          </p>
        </div>
      </div>
      
      {transaction.reason && (
        <p className="text-sm text-zinc-400 mt-2 pl-13 border-l-2 border-zinc-700 ml-5 pl-3">
          {transaction.reason}
        </p>
      )}
      
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-800">
        <span className="text-xs text-zinc-500 flex items-center gap-1">
          <User className="w-3 h-3" /> Por: {transaction.admin_name}
        </span>
        <span className="text-xs text-zinc-500 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {new Date(transaction.created_at).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </span>
      </div>
    </motion.div>
  );
};

// Componente principal
export const BalanceManager = ({ users, onRefresh }) => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  
  // Filtros
  const [filterUserId, setFilterUserId] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterOperation, setFilterOperation] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchTransactions = async () => {
    try {
      const params = new URLSearchParams();
      if (filterUserId) params.append('user_id', filterUserId);
      if (filterType) params.append('balance_type', filterType);
      if (filterOperation) params.append('operation', filterOperation);
      
      const [transRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/balance-transactions?${params.toString()}`),
        axios.get(`${API}/admin/balance-transactions/stats`)
      ]);
      
      setTransactions(transRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Erro ao carregar transações:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [filterUserId, filterType, filterOperation]);

  const handleSuccess = () => {
    fetchTransactions();
    onRefresh();
  };

  // Filtrar transações por busca
  const filteredTransactions = useMemo(() => {
    if (!searchTerm) return transactions;
    const search = searchTerm.toLowerCase();
    return transactions.filter(t => 
      t.user_name?.toLowerCase().includes(search) ||
      t.user_email?.toLowerCase().includes(search) ||
      t.reason?.toLowerCase().includes(search) ||
      t.admin_name?.toLowerCase().includes(search)
    );
  }, [transactions, searchTerm]);

  // Usuários não-admin para o seletor
  const selectableUsers = users.filter(u => u.role !== 'admin');

  return (
    <div className="space-y-6" data-testid="balance-manager">
      {/* Header com estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-zinc-400 mb-1">
            <History className="w-4 h-4" />
            <span className="text-xs">Total Transações</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats?.total_transactions || 0}</p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-zinc-400 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs">Hoje</span>
          </div>
          <p className="text-2xl font-bold text-white">{stats?.today_transactions || 0}</p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-amber-400 mb-1">
            <img src="/fercoin.png" alt="FerCoin" className="w-4 h-4" />
            <span className="text-xs">FerCoins Movimentados</span>
          </div>
          <p className="text-lg font-bold text-green-400">+{stats?.coins_added?.toFixed(0) || 0}</p>
          <p className="text-sm text-red-400">-{stats?.coins_removed?.toFixed(0) || 0}</p>
        </div>
        <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-4">
          <div className="flex items-center gap-2 text-neon-purple mb-1">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs">Pontos Movimentados</span>
          </div>
          <p className="text-lg font-bold text-green-400">+{stats?.points_added?.toFixed(0) || 0}</p>
          <p className="text-sm text-red-400">-{stats?.points_removed?.toFixed(0) || 0}</p>
        </div>
      </div>

      {/* Ações e Filtros */}
      <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Coins className="w-8 h-8 text-neon-purple" />
            <div>
              <h3 className="font-primary font-bold text-xl text-white">Gerenciar Saldos</h3>
              <p className="text-zinc-500 text-sm">Ajuste pontos e FerCoins dos usuários</p>
            </div>
          </div>
          <Button
            onClick={() => setShowAdjustModal(true)}
            className="bg-neon-purple hover:bg-neon-purple/80 text-white"
            data-testid="open-adjust-modal-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Nova Transação
          </Button>
        </div>

        {/* Filtros */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar..."
              className="pl-10 bg-zinc-950/50 border-zinc-800 text-white"
              data-testid="search-transactions-input"
            />
          </div>
          <select
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            className="bg-zinc-950/50 border border-zinc-800 text-white rounded-lg px-3 py-2"
            data-testid="filter-user-select"
          >
            <option value="">Todos os usuários</option>
            {selectableUsers.map(user => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="bg-zinc-950/50 border border-zinc-800 text-white rounded-lg px-3 py-2"
            data-testid="filter-type-select"
          >
            <option value="">Todos os tipos</option>
            <option value="coins">FerCoins</option>
            <option value="points">Pontos (XP)</option>
          </select>
          <select
            value={filterOperation}
            onChange={(e) => setFilterOperation(e.target.value)}
            className="bg-zinc-950/50 border border-zinc-800 text-white rounded-lg px-3 py-2"
            data-testid="filter-operation-select"
          >
            <option value="">Todas as operações</option>
            <option value="add">Adições</option>
            <option value="remove">Remoções</option>
          </select>
        </div>

        {/* Lista de transações */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-neon-purple border-t-transparent rounded-full mx-auto mb-3" />
            <p className="text-zinc-500">Carregando histórico...</p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-8">
            <History className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">
              {transactions.length === 0 
                ? 'Nenhuma transação registrada ainda' 
                : 'Nenhuma transação encontrada com os filtros aplicados'}
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto" data-testid="transactions-list">
            {filteredTransactions.map(transaction => (
              <TransactionCard key={transaction.id} transaction={transaction} />
            ))}
          </div>
        )}
      </div>

      {/* Modal de ajuste */}
      <AnimatePresence>
        {showAdjustModal && (
          <AdjustBalanceModal
            users={selectableUsers}
            onClose={() => setShowAdjustModal(false)}
            onSuccess={handleSuccess}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
